import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Divider, Drawer, Group, ScrollArea, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { obligationInputSchema, type ObligationInputSchema } from '../../../lib/schemas';
import { useCreateObligation, useUpdateObligation } from '../../../queries/obligations';
import { useObligationFormLayout } from '../../../queries/obligation-form-layout';
import { useProjectMembers } from '../../../queries/project-members';
import { useProjectStore } from '../../../project/project-store';
import {
  OBLIGATION_FORM_FIELDS,
  OBLIGATION_FORM_FIELD_NAMES,
  OBLIGATION_DEFAULT_LAYOUT,
  type ObligationFormContext,
} from '../../../lib/form-layout/obligation-fields';
import { FormLayoutRenderer } from '../../../lib/form-layout/FormLayoutRenderer';
import { ApiError } from '../../../lib/api-error';
import type { Obligation } from '../../../types/api';

interface Props {
  opened: boolean;
  onClose: () => void;
  obligation?: Obligation | null;
  onSaved?: (o: Obligation) => void;
}

const EMPTY: ObligationInputSchema = {
  name: '',

  applies_to_lease: false,
  applies_to_contract: false,
  applies_to_well: false,

  value_type: 'none',

  attention: null,
  address_1: null,
  address_2: null,
  city: null,
  state: null,
  zip: null,
  phone: null,
  fax: null,
  email: null,

  remarks: null,
};

function fromObligation(o: Obligation): ObligationInputSchema {
  return {
    name: o.name,

    applies_to_lease: o.applies_to_lease,
    applies_to_contract: o.applies_to_contract,
    applies_to_well: o.applies_to_well,

    value_type: o.value_type,

    attention: o.attention,
    address_1: o.address_1,
    address_2: o.address_2,
    city: o.city,
    state: o.state,
    zip: o.zip,
    phone: o.phone,
    fax: o.fax,
    email: o.email,

    remarks: o.remarks,
  };
}

const EMPTY_CONTEXT: ObligationFormContext = {};

export function ObligationFormDrawer({ opened, onClose, obligation, onSaved }: Props) {
  const isEdit = !!obligation;
  const createM = useCreateObligation();
  const updateM = useUpdateObligation();
  const projectId = useProjectStore((s) => s.projectId);

  const layoutQ = useObligationFormLayout(projectId);
  const layout = layoutQ.data?.layout ?? OBLIGATION_DEFAULT_LAYOUT;

  // Project members double as the "users lookup" for resolving the display
  // name behind updated_by_user_id (obligations have no reviewer_user_id, so
  // this is the only membership-derived lookup this drawer needs).
  const { data: members } = useProjectMembers(projectId);

  const updatedByName = obligation?.updated_by_user_id
    ? (members?.find((m) => m.user_id === obligation.updated_by_user_id)?.username ??
      obligation.updated_by_user_id)
    : null;

  const [lastOpenKey, setLastOpenKey] = useState<string | null>(null);

  const form = useForm<ObligationInputSchema>({
    resolver: zodResolver(obligationInputSchema),
    defaultValues: EMPTY,
  });

  const openKey = opened ? `${obligation?.id ?? 'new'}-${obligation?.updated_at ?? ''}` : null;
  if (openKey !== lastOpenKey) {
    setLastOpenKey(openKey);
    if (opened) form.reset(obligation ? fromObligation(obligation) : EMPTY);
  }

  async function onSubmit(values: ObligationInputSchema) {
    try {
      if (isEdit && obligation) {
        const res = await updateM.mutateAsync({ id: obligation.id, patch: values });
        notifications.show({
          title: 'Obligation saved',
          message: res.obligation.name,
          color: 'pine',
        });
        onSaved?.(res.obligation);
        onClose();
      } else {
        const res = await createM.mutateAsync(values);
        notifications.show({
          title: 'Obligation created',
          message: res.obligation.name,
          color: 'pine',
        });
        onSaved?.(res.obligation);
        onClose();
      }
    } catch (err) {
      notifications.show({
        title: 'Save failed',
        message: err instanceof ApiError ? err.message : 'Unexpected error',
        color: 'red',
      });
    }
  }

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="xl"
      padding="md"
      title={<Title order={4}>{isEdit ? 'Edit obligation' : 'New obligation'}</Title>}
      scrollAreaComponent={ScrollArea.Autosize}
      overlayProps={{ backgroundOpacity: 0.15 }}
    >
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Stack gap="md">
          <FormLayoutRenderer
            layout={layout}
            control={form.control}
            register={form.register}
            registry={OBLIGATION_FORM_FIELDS}
            allNames={OBLIGATION_FORM_FIELD_NAMES}
            context={EMPTY_CONTEXT}
            isEdit={isEdit}
          />

          {isEdit && obligation && (
            <>
              <Divider />
              <Text size="sm" c="dimmed">
                {`Last updated by ${updatedByName ?? '—'} on ${new Date(
                  obligation.updated_at,
                ).toLocaleDateString()}`}
              </Text>
            </>
          )}

          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={onClose} type="button">
              Cancel
            </Button>
            <Button type="submit" loading={form.formState.isSubmitting}>
              {isEdit ? 'Save changes' : 'Create obligation'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}
